/**
 * Ad-review safety lint — server-side source of truth (D-22 / D-32).
 *
 * Encodes the rules from `docs/AD_REVIEW_LINT.md`
 * as a `json-rules-engine` rule set. Runs identically client-side (admin SPA
 * preview) and server-side (admin-api publish endpoint) so there's a single
 * library and a single source of truth (PLAN.md §6.6).
 *
 * Lint failures with `severity: "error"` block paid-ad publishing. Direct /
 * SEO / affiliate campaigns get warnings but are not blocked (per the doc).
 */

import { evaluateRules } from "@rules/index";
import type { Rule, RuleFacts } from "@rules/types";

export type LintSeverity = "error" | "warning";

export type LintViolation = {
  readonly ruleId: string;
  readonly severity: LintSeverity;
  readonly message: string;
  readonly source: string;
  readonly category:
    | "forbidden_phrase"
    | "conditional_phrase"
    | "age_bucket_framing"
    | "forbidden_imagery_tag"
    | "forbidden_mechanic";
};

export type LintReport = {
  readonly violations: ReadonlyArray<LintViolation>;
  readonly errorCount: number;
  readonly warningCount: number;
  readonly blocking: boolean;
};

/**
 * Funnel surface area we lint over. Callers (admin-api publish endpoint,
 * admin SPA preview) flatten their data model into this shape so the lint
 * doesn't take a hard dep on D1 schemas.
 */
export type AdReviewLintInput = {
  /** All ad-facing copy strings in the funnel + variants, concatenated. */
  readonly copy: string;
  /** Tags applied to creatives used in this funnel. */
  readonly imageryTags: ReadonlyArray<string>;
  /** Step-level mechanic flags lifted from `funnel_steps.config_json`. */
  readonly mechanics: {
    readonly loadingScreenContinueDelaySec?: number;
    readonly hasFakeCountdown?: boolean;
    readonly hasUnverifiedStrikethrough?: boolean;
    readonly autoRenewalAboveIntroNoDisclosure?: boolean;
    readonly hasPaymentRetryAtLowerPrices?: boolean;
  };
  /** Tags on the funnel itself — used by conditional-phrase rules (§2). */
  readonly funnelTags: ReadonlyArray<string>;
  /** Whether any paid-ad network inlet references this funnel. */
  readonly hasPaidNetwork: boolean;
};

// §1 — Forbidden phrases (case-insensitive substring)
const FORBIDDEN_PHRASES: ReadonlyArray<{
  phrase: string;
  source: string;
}> = [
  { phrase: "MILF", source: "AD_REVIEW_LINT.md §1" },
  { phrase: "Step sister", source: "AD_REVIEW_LINT.md §1" },
  { phrase: "Step mom", source: "AD_REVIEW_LINT.md §1" },
  { phrase: "Sex friend", source: "AD_REVIEW_LINT.md §1" },
  { phrase: "Uncensored", source: "AD_REVIEW_LINT.md §1" },
  { phrase: "Spicy pics", source: "AD_REVIEW_LINT.md §1" },
  { phrase: "Spicy chats", source: "AD_REVIEW_LINT.md §1" },
  { phrase: "Steamy videos", source: "AD_REVIEW_LINT.md §1" },
  { phrase: "Special photos", source: "AD_REVIEW_LINT.md §1" },
  { phrase: "Private videos", source: "AD_REVIEW_LINT.md §1" },
  { phrase: "One night stand", source: "AD_REVIEW_LINT.md §1" },
  { phrase: "Daddy Dominance", source: "AD_REVIEW_LINT.md §1" },
];

// §2 — Conditional phrases (only flag on adult-tagged funnels)
const CONDITIONAL_PHRASES: ReadonlyArray<{
  phrase: string;
  source: string;
  message: string;
}> = [
  {
    phrase: "Innocent",
    source: "AD_REVIEW_LINT.md §2",
    message:
      "'Innocent' as a personality / voice label on an adult-tagged funnel reads as grooming-adjacent.",
  },
  {
    phrase: "Submissive",
    source: "AD_REVIEW_LINT.md §2",
    message:
      "'Submissive' as a default personality pill on a paid-ad funnel is flagged for review.",
  },
];

// §4 — Forbidden imagery tags
const FORBIDDEN_IMAGERY_TAGS: ReadonlyArray<string> = [
  "nudity",
  "lingerie_only",
  "body_part_close_up",
  "undressed_silhouette",
  "kink_props",
];

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const phraseRule = (
  category: LintViolation["category"],
  ruleId: string,
  phrase: string,
  source: string,
  message: string,
): Rule => ({
  id: ruleId,
  name: ruleId,
  priority: 10,
  conditions: {
    all: [
      {
        fact: "copy",
        operator: "regexMatches",
        value: { pattern: escapeRegExp(phrase), flags: "i" },
      },
    ],
  },
  event: {
    type: "lint_violation",
    params: { severity: "error", message, source, category },
  },
});

// Escape regex metachars so user phrases like "Daddy Dominance" don't blow up.
const escapeRegExp = (input: string): string => input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** §1 rules — always block when phrase appears anywhere in copy. */
const forbiddenPhraseRules: ReadonlyArray<Rule> = FORBIDDEN_PHRASES.map(({ phrase, source }) =>
  phraseRule(
    "forbidden_phrase",
    `forbidden_phrase_${slug(phrase)}`,
    phrase,
    source,
    `Forbidden phrase '${phrase}' appears in funnel copy.`,
  ),
);

/** §2 rules — only fire when the funnel carries the `adult` tag. */
const conditionalPhraseRules: ReadonlyArray<Rule> = CONDITIONAL_PHRASES.map(
  ({ phrase, source, message }) => ({
    id: `conditional_phrase_${slug(phrase)}`,
    name: `conditional_phrase_${slug(phrase)}`,
    priority: 8,
    conditions: {
      all: [
        { fact: "funnelTags", operator: "contains", value: "adult" },
        {
          fact: "copy",
          operator: "regexMatches",
          value: { pattern: escapeRegExp(phrase), flags: "i" },
        },
      ],
    },
    event: {
      type: "lint_violation",
      params: {
        severity: "error" satisfies LintSeverity,
        message,
        source,
        category: "conditional_phrase" satisfies LintViolation["category"],
      },
    },
  }),
);

/** §3 — age-bucket framings (regex catches "<digits><non-word>+<flagged-word>"). */
const ageBucketFramingRules: ReadonlyArray<Rule> = [
  {
    id: "age_bucket_sexualized_label",
    name: "age_bucket_sexualized_label",
    priority: 9,
    conditions: {
      all: [
        {
          fact: "copy",
          operator: "regexMatches",
          value: {
            pattern:
              "\\b\\d{2,3}\\+?(?:\\s*-\\s*\\d{2,3})?\\s+(MILF|Hot|Playful|Submissive|Innocent)\\b",
            flags: "i",
          },
        },
      ],
    },
    event: {
      type: "lint_violation",
      params: {
        severity: "error" satisfies LintSeverity,
        message:
          "Sexualizing label attached to an age range (e.g., '36+ MILF', '18-24 Playful'). Use a neutral descriptor.",
        source: "AD_REVIEW_LINT.md §3",
        category: "age_bucket_framing" satisfies LintViolation["category"],
      },
    },
  },
];

/** §4 — forbidden imagery tags. */
const forbiddenImageryRules: ReadonlyArray<Rule> = FORBIDDEN_IMAGERY_TAGS.map((tag) => ({
  id: `forbidden_imagery_${tag}`,
  name: `forbidden_imagery_${tag}`,
  priority: 7,
  conditions: {
    all: [{ fact: "imageryTags", operator: "contains", value: tag }],
  },
  event: {
    type: "lint_violation",
    params: {
      severity: "error" satisfies LintSeverity,
      message: `Creative tagged '${tag}' cannot fill a slot in a paid-ad funnel.`,
      source: "AD_REVIEW_LINT.md §4",
      category: "forbidden_imagery_tag" satisfies LintViolation["category"],
    },
  },
}));

/** §5 — forbidden mechanics. */
const forbiddenMechanicRules: ReadonlyArray<Rule> = [
  {
    id: "loading_screen_disabled_continue_too_long",
    name: "loading_screen_disabled_continue_too_long",
    priority: 6,
    conditions: {
      all: [
        {
          fact: "mechanics",
          path: "$.loadingScreenContinueDelaySec",
          operator: "greaterThan",
          value: 30,
        },
      ],
    },
    event: {
      type: "lint_violation",
      params: {
        severity: "error" satisfies LintSeverity,
        message:
          "Loading screen with disabled-continue > 30s is a dark-pattern threshold (Dialogue audit §6).",
        source: "AD_REVIEW_LINT.md §5",
        category: "forbidden_mechanic" satisfies LintViolation["category"],
      },
    },
  },
  {
    id: "fake_countdown",
    name: "fake_countdown",
    priority: 6,
    conditions: {
      all: [{ fact: "mechanics", path: "$.hasFakeCountdown", operator: "equal", value: true }],
    },
    event: {
      type: "lint_violation",
      params: {
        severity: "error" satisfies LintSeverity,
        message: "Countdown timer without a real, paywall-honored expiry is forbidden (D-27).",
        source: "AD_REVIEW_LINT.md §5",
        category: "forbidden_mechanic" satisfies LintViolation["category"],
      },
    },
  },
  {
    id: "unverified_strikethrough_pricing",
    name: "unverified_strikethrough_pricing",
    priority: 6,
    conditions: {
      all: [
        {
          fact: "mechanics",
          path: "$.hasUnverifiedStrikethrough",
          operator: "equal",
          value: true,
        },
      ],
    },
    event: {
      type: "lint_violation",
      params: {
        severity: "error" satisfies LintSeverity,
        message:
          "Strikethrough pricing requires a 30-day verifiable history (EU Omnibus Directive Art. 6a).",
        source: "AD_REVIEW_LINT.md §5",
        category: "forbidden_mechanic" satisfies LintViolation["category"],
      },
    },
  },
  {
    id: "auto_renewal_above_intro_no_disclosure",
    name: "auto_renewal_above_intro_no_disclosure",
    priority: 6,
    conditions: {
      all: [
        {
          fact: "mechanics",
          path: "$.autoRenewalAboveIntroNoDisclosure",
          operator: "equal",
          value: true,
        },
      ],
    },
    event: {
      type: "lint_violation",
      params: {
        severity: "error" satisfies LintSeverity,
        message:
          "Auto-renewal pricing higher than intro pricing requires inline disclosure above the CTA (FTC ROSCA / EU Omnibus / §312j BGB / California ARL).",
        source: "AD_REVIEW_LINT.md §5",
        category: "forbidden_mechanic" satisfies LintViolation["category"],
      },
    },
  },
  {
    id: "payment_retry_at_lower_prices",
    name: "payment_retry_at_lower_prices",
    priority: 6,
    conditions: {
      all: [
        {
          fact: "mechanics",
          path: "$.hasPaymentRetryAtLowerPrices",
          operator: "equal",
          value: true,
        },
      ],
    },
    event: {
      type: "lint_violation",
      params: {
        severity: "error" satisfies LintSeverity,
        message:
          "Payment-retry mechanisms at progressively lower prices are illegal in Germany (§312j BGB).",
        source: "AD_REVIEW_LINT.md §5",
        category: "forbidden_mechanic" satisfies LintViolation["category"],
      },
    },
  },
];

/** Frozen rule set — exported so the admin SPA can run the same lint client-side. */
export const AD_REVIEW_LINT_RULES: ReadonlyArray<Rule> = [
  ...forbiddenPhraseRules,
  ...conditionalPhraseRules,
  ...ageBucketFramingRules,
  ...forbiddenImageryRules,
  ...forbiddenMechanicRules,
];

const buildFacts = (input: AdReviewLintInput): RuleFacts => ({
  copy: input.copy,
  imageryTags: input.imageryTags,
  mechanics: input.mechanics,
  funnelTags: input.funnelTags,
  hasPaidNetwork: input.hasPaidNetwork,
});

const eventToViolation = (event: {
  type: string;
  params?: Record<string, unknown>;
}): LintViolation | null => {
  if (event.type !== "lint_violation") return null;
  const params = event.params ?? {};
  return {
    ruleId: typeof params.ruleId === "string" ? params.ruleId : "",
    severity: params.severity === "warning" ? "warning" : ("error" satisfies LintSeverity),
    message: typeof params.message === "string" ? params.message : "",
    source: typeof params.source === "string" ? params.source : "",
    category:
      typeof params.category === "string"
        ? (params.category as LintViolation["category"])
        : "forbidden_phrase",
  };
};

/**
 * Run the full ad-review lint over a funnel + variants snapshot. Returns a
 * structured report. `blocking: true` ⇒ admin-api publish endpoint must
 * reject the publish for any paid-ad campaign.
 */
export const evaluateAdReviewLint = async (input: AdReviewLintInput): Promise<LintReport> => {
  const result = await evaluateRules(AD_REVIEW_LINT_RULES, buildFacts(input));

  const violations: LintViolation[] = [];
  result.events.forEach((event, idx) => {
    const violation = eventToViolation(event);
    if (violation === null) return;
    const ruleId = result.firedRuleIds[idx] ?? violation.ruleId;
    violations.push({ ...violation, ruleId });
  });

  const errorCount = violations.filter((v) => v.severity === "error").length;
  const warningCount = violations.filter((v) => v.severity === "warning").length;
  return {
    violations,
    errorCount,
    warningCount,
    blocking: input.hasPaidNetwork && errorCount > 0,
  };
};
